/**
 * Sales Team Report exports.
 *
 * RECENT mode keeps the original hand-built 13-column CSV, byte-for-byte: the
 * same reasoning that protects the default view protects the default export,
 * and whoever consumes that file downstream has a known filename and shape.
 *
 * RANGE mode gets a two-sheet .xlsx:
 *   "Range Summary" — the familiar 13 columns, now meaning the whole range.
 *   "Weekly"        — LONG/TIDY: one row per (row x week bucket).
 *
 * The weekly sheet is deliberately long rather than wide. Wide x four metrics
 * would be up to 64 dynamic columns (unreadable, and it breaks autoFilter);
 * wide x the selected metric would bake a transient UI choice into a file that
 * then cannot answer "what about Leads?". Long format has one fixed header row,
 * pivots in three clicks, carries all four metrics, and is immune to how many
 * week buckets the range produced.
 */

import { loadExcelJS } from '../../../utils/loadExcelJS';
import {
  FunnelPeriodMetrics,
  SalesFunnelTotalsP,
  SalesTeamFunnelReportData,
} from '../../../types/salesTeamFunnel';
import { formatLongDate, inclusiveDayCount } from '../../../utils/localDate';

const pct = (v: number | null) => (v === null ? '' : `${v.toFixed(1)}%`);

/**
 * Always-quoted CSV field, with embedded quotes doubled.
 *
 * Deliberately NOT `escapeCSV` from utils/reportExport, which quotes only when
 * a comma/quote/newline forces it. The pre-existing recent-mode exporter wrapped
 * Location / Team / Salesperson in quotes unconditionally, and downstream
 * consumers (and any snapshot comparison) see that shape — so "byte-identical"
 * means keeping it. The `.replace` is the one improvement over the original,
 * which interpolated raw and would emit malformed CSV for a name containing a
 * double quote.
 */
const q = (v: string) => `"${String(v).replace(/"/g, '""')}"`;

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  // Appended before click — Safari ignores a click on a detached anchor.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** True when the report's end date is not in the current month. */
function isHistoric(reportDate: string): boolean {
  const now = new Date();
  return !reportDate.startsWith(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
  );
}

const SUMMARY_HEADERS = [
  'Location', 'Team', 'Salesperson',
  'Leads', 'Visits', 'Appts', 'Sales',
  'Visits/Lead %', 'Sales/Visit %', 'Sales/Lead %',
  'Active Leads', 'Next 3 (incl. report date)', 'Next 5 (incl. report date)',
];

/**
 * Build the recent-mode CSV exactly as the pre-range-mode exporter did.
 *
 * Pure and exported so the byte-level shape is regression-tested directly
 * rather than only through the download side effect.
 *
 * Note the deliberate asymmetry inherited from the original: Location, Team and
 * Salesperson are always quoted, while the literal row labels (SUBTOTAL,
 * LOCATION TOTAL, ALL LOCATIONS, GRAND TOTAL), the empty team cell and every
 * numeric cell are not.
 */
export function buildRecentCsv(data: SalesTeamFunnelReportData): string {
  const rows: string[] = [];
  // Active Leads is point-in-time: on a past-month report every row is null,
  // so aggregate cells export blank (matching the renderer's em dash), not 0.
  const historic = isHistoric(data.reportDate);
  const totalsCells = (t: SalesFunnelTotalsP) => [
    String(t.leads), String(t.visits), String(t.apptsPast), String(t.sales),
    pct(t.visitsPerLeadPct), pct(t.salesPerVisitPct), pct(t.salesPerLeadPct),
    historic ? '' : String(t.activeLeads), String(t.apptsNext3), String(t.apptsNext5),
  ];

  rows.push('Report: Sales Team Report');
  rows.push(`Report Date: ${data.reportDate}`);
  rows.push(`Generated: ${new Date().toLocaleDateString()}`);
  rows.push('');
  rows.push(SUMMARY_HEADERS.join(','));

  for (const loc of data.locations) {
    for (const team of loc.teams) {
      const teamLabel = team.managerId ? `Team ${team.managerName}` : 'Unassigned';
      for (const row of team.rows) {
        rows.push([
          q(loc.locationName), q(teamLabel), q(row.name),
          String(row.leads), String(row.visits), String(row.apptsPast), String(row.sales),
          pct(row.visitsPerLeadPct), pct(row.salesPerVisitPct), pct(row.salesPerLeadPct),
          row.activeLeads === null ? '' : String(row.activeLeads),
          String(row.apptsNext3), String(row.apptsNext5),
        ].join(','));
      }
      rows.push([q(loc.locationName), q(teamLabel), 'SUBTOTAL', ...totalsCells(team.subtotals)].join(','));
    }
    rows.push([q(loc.locationName), '', 'LOCATION TOTAL', ...totalsCells(loc.totals)].join(','));
  }
  if (data.locations.length > 1) {
    rows.push(['ALL LOCATIONS', '', 'GRAND TOTAL', ...totalsCells(data.grandTotals)].join(','));
  }

  return rows.join('\n');
}

/** RECENT mode: the original CSV, unchanged. */
export function exportRecentCsv(data: SalesTeamFunnelReportData): void {
  triggerDownload(
    new Blob([buildRecentCsv(data)], { type: 'text/csv;charset=utf-8;' }),
    `sales-team-report-${data.reportDate}.csv`,
  );
}

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** RANGE mode: a two-sheet workbook. Async — exceljs is a lazy chunk. */
export async function exportRangeWorkbook(data: SalesTeamFunnelReportData): Promise<void> {
  if (!data.periods || !data.rangeStartDate || !data.rangeEndDate) {
    throw new Error('exportRangeWorkbook requires a range-mode report');
  }
  const ExcelJS = await loadExcelJS();
  const wb = new ExcelJS.Workbook();
  wb.created = new Date();

  const periods = data.periods;
  const historic = isHistoric(data.reportDate);
  const spanDays = inclusiveDayCount(data.rangeStartDate, data.rangeEndDate);

  const headerStyle = (row: any) => {
    row.font = { bold: true };
    row.alignment = { vertical: 'middle', wrapText: true };
  };

  // ── Sheet 1: Range Summary ────────────────────────────────────────────────
  const summary = wb.addWorksheet('Range Summary', {
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    views: [{ state: 'frozen', xSplit: 3, ySplit: 6 }],
  });

  summary.addRow([`Sales Team Report — ${data.locations.length} location(s)`]);
  summary.addRow([
    `Range: ${formatLongDate(data.rangeStartDate)} – ${formatLongDate(data.rangeEndDate)}`
    + ` (${spanDays} days · ${periods.length} week bucket${periods.length === 1 ? '' : 's'})`,
  ]);
  summary.addRow([`Generated: ${new Date().toLocaleString()}`]);
  summary.addRow([
    'Active Leads is a point-in-time count as of today, not a range figure.'
    + (data.apptsPastWindow
      ? ` Appts cover ${data.apptsPastWindow.startDate} through ${data.apptsPastWindow.endDate};`
        + ` appointments on ${data.rangeEndDate} are counted under Next 3 / Next 5.`
      // One-day range: the past-appointment window is empty, so apptsPastWindow
      // is absent rather than a reversed pair.
      : ` This range is a single day, so every appointment on ${data.rangeEndDate} is counted`
        + ' under Next 3 / Next 5 and the Appts column is empty by definition.'),
  ]);
  summary.addRow([]);
  headerStyle(summary.addRow(SUMMARY_HEADERS));
  summary.getRow(1).font = { bold: true, size: 14 };

  const totalsCells = (t: SalesFunnelTotalsP) => [
    t.leads, t.visits, t.apptsPast, t.sales,
    pct(t.visitsPerLeadPct), pct(t.salesPerVisitPct), pct(t.salesPerLeadPct),
    historic ? '' : t.activeLeads, t.apptsNext3, t.apptsNext5,
  ];

  for (const loc of data.locations) {
    for (const team of loc.teams) {
      const teamLabel = team.managerId ? `Team ${team.managerName}` : 'Unassigned';
      for (const row of team.rows) {
        summary.addRow([
          loc.locationName, teamLabel, row.name,
          row.leads, row.visits, row.apptsPast, row.sales,
          pct(row.visitsPerLeadPct), pct(row.salesPerVisitPct), pct(row.salesPerLeadPct),
          row.activeLeads === null ? '' : row.activeLeads,
          row.apptsNext3, row.apptsNext5,
        ]);
      }
      summary.addRow([loc.locationName, teamLabel, 'SUBTOTAL', ...totalsCells(team.subtotals)])
        .font = { bold: true };
    }
    summary.addRow([loc.locationName, '', 'LOCATION TOTAL', ...totalsCells(loc.totals)])
      .font = { bold: true };
  }
  if (data.locations.length > 1) {
    summary.addRow(['ALL LOCATIONS', '', 'GRAND TOTAL', ...totalsCells(data.grandTotals)])
      .font = { bold: true };
  }

  summary.columns.forEach((col: any, i: number) => {
    col.width = i < 3 ? 26 : 14;
  });
  if (summary.rowCount > 6) {
    summary.autoFilter = {
      from: { row: 6, column: 1 },
      to: { row: summary.rowCount, column: SUMMARY_HEADERS.length },
    };
  }

  // ── Sheet 2: Weekly (long / tidy) ─────────────────────────────────────────
  const weeklyHeaders = [
    'Location', 'Team', 'Name', 'Row Type', 'Week', 'Week Dates',
    'Start', 'End', 'Days', 'Partial', 'Leads', 'Visits', 'Appts', 'Sales',
  ];
  const weekly = wb.addWorksheet('Weekly', {
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  headerStyle(weekly.addRow(weeklyHeaders));

  const addWeekRows = (
    locationName: string,
    teamLabel: string,
    name: string,
    rowType: string,
    metrics: FunnelPeriodMetrics[] | undefined,
    bold = false,
  ) => {
    periods.forEach((period, i) => {
      const m = metrics?.[i];
      const added = weekly.addRow([
        locationName, teamLabel, name, rowType,
        period.label, period.sublabel, period.startDate, period.endDate,
        period.days, period.partial ? 'Yes' : '',
        // Blank rather than 0 when the row carries no per-week data, matching
        // the renderer's em dash — "unknown" must not read as "sold nothing".
        m ? m.leads : '', m ? m.visits : '', m ? m.apptsPast : '', m ? m.sales : '',
      ]);
      if (bold) added.font = { bold: true };
    });
  };

  for (const loc of data.locations) {
    for (const team of loc.teams) {
      const teamLabel = team.managerId ? `Team ${team.managerName}` : 'Unassigned';
      for (const row of team.rows) {
        addWeekRows(loc.locationName, teamLabel, row.name, 'Salesperson', row.periods);
      }
      addWeekRows(loc.locationName, teamLabel, teamLabel, 'Team Subtotal', team.subtotals.periods, true);
    }
    addWeekRows(loc.locationName, '', loc.locationName, 'Location Total', loc.totals.periods, true);
  }
  if (data.locations.length > 1) {
    addWeekRows('ALL LOCATIONS', '', 'All Selected Locations', 'Grand Total', data.grandTotals.periods, true);
  }

  weekly.columns.forEach((col: any, i: number) => {
    col.width = i < 4 ? 24 : 13;
  });
  if (weekly.rowCount > 1) {
    weekly.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: weekly.rowCount, column: weeklyHeaders.length },
    };
  }

  const buffer = await wb.xlsx.writeBuffer();
  triggerDownload(
    new Blob([buffer], { type: XLSX_MIME }),
    `sales-team-report-${data.rangeStartDate}_to_${data.rangeEndDate}.xlsx`,
  );
}
